import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import HotelEventsFormFieldsEventSearchField from 'lumen/components/hotel/events/form-fields/event-search-field';
import HotelEventsHolidayConfiguration from 'lumen/components/hotel/events/holiday-configuration';
import type { SearchResults } from 'lumen/controllers/hotel/events';
import can from 'lumen/helpers/can';
import { EVENTS_STATUSES } from 'lumen/models/event';
import { EVENTS_CATEGORIES_WITH_LABEL_AND_ICON } from 'lumen/utils/constants/events-constants';

type EventsFilterOption = Option & {
  id: string;
  label: string;
};

interface HotelEventsSelectionBarFiltersSignature {
  Args: {
    addExistingEvents: (event: { added: boolean; id: string }) => void;
    fetchSearchResult: (input: string, page?: number) => Promise<void>;
    resetEvents: () => void;
    searchResults: SearchResults;
    selectedEventsCategories: string[];
    selectedEventsStatuses: string[];
    selectionsChanged: (selection: string, value: string[]) => void;
  };
}

export default class HotelEventsSelectionBarFilters extends Component<HotelEventsSelectionBarFiltersSignature> {
  @service declare intl: Services['intl'];

  @tracked searchActive = false;

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventCategoryOptions() {
    return [
      ...EVENTS_CATEGORIES_WITH_LABEL_AND_ICON.map((event) => ({
        label: this.intl.t(event.translationKey),
        id: event.id,
        icon: event.icon,
      })).sort((a, b) => a.label.localeCompare(b.label)),
      {
        id: 'other',
        label: this.intl.t('events.category.other'),
      },
    ];
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventStatusOptions() {
    return [
      {
        label: this.intl.t('events.accepted_events'),
        id: EVENTS_STATUSES.ACCEPTED,
      },
      {
        label: this.intl.t('events.declined_events'),
        id: EVENTS_STATUSES.DECLINED,
      },
      {
        label: this.intl.t('events.suggested_events'),
        id: EVENTS_STATUSES.SUGGESTED,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedEventCategoryItems() {
    const selectedCategories = this.eventCategoryOptions.filter((filter) =>
      this.args.selectedEventsCategories.includes(filter.id),
    );

    if (selectedCategories.length > 0) {
      return selectedCategories;
    }

    return this.eventCategoryOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedEventsStatusItems() {
    const selectedFilters = this.eventStatusOptions.filter((filter) =>
      this.args.selectedEventsStatuses.includes(filter.id),
    );

    if (selectedFilters.length > 0) {
      return selectedFilters;
    }

    return [
      {
        label: this.intl.t('events.suggested_events'),
        id: EVENTS_STATUSES.SUGGESTED,
      },
    ];
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  categoriesLabelWithCount(count: number) {
    return this.intl.t('events.filter.categories.count', { count });
  }

  @action
  chooseEventsCategories(eventsCategories: Option[]): void {
    const typedOptions = eventsCategories.map(
      (category) => (category as EventsFilterOption).id,
    );
    this.args.selectionsChanged('eventsCategories', typedOptions);
  }

  @action
  chooseEventsFilter(options: EventsFilterOption[]): void {
    const typedOptionIds = options.map((option) => option.id);
    this.args.selectionsChanged('eventsFilter', typedOptionIds);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  eventsLabelWithCount(count: number) {
    return this.intl.t('events.filter.count', { count });
  }

  @action
  setActive(event: Event): void {
    event.preventDefault();
    /*
      Search icon needs separate function, because this can not handle closing the dropdown first
      So it can not simply remove the elemnts, only show them
    */
    this.searchActive = true;
  }

  @action
  toggleSearch(): void {
    this.searchActive = !this.searchActive;
  }

  <template>
    <div class="flex gap-200">
      <PrismComboSelectInput
        @buildLabelWithCount={{this.eventsLabelWithCount}}
        @onChange={{this.chooseEventsFilter}}
        @options={{this.eventStatusOptions}}
        @selectAllLabel={{t "events.all_events"}}
        @value={{this.selectedEventsStatusItems}}
      />
      <PrismComboSelectInput
        @buildLabelWithCount={{this.categoriesLabelWithCount}}
        @onChange={{this.chooseEventsCategories}}
        @options={{this.eventCategoryOptions}}
        @selectAllLabel={{t "events.filter.all_categories"}}
        @value={{this.selectedEventCategoryItems}}
      />
      {{#if (can "events_holidays.author")}}
        {{! @glint-expect-error: Incorrect type }}
        <HotelEventsHolidayConfiguration @resetEvents={{@resetEvents}} />
        <div>
          {{#if this.searchActive}}
            <HotelEventsFormFieldsEventSearchField
              @addExistingEvents={{@addExistingEvents}}
              @fetchSearchResult={{@fetchSearchResult}}
              @onToggleSearch={{this.toggleSearch}}
              @resetEvents={{@resetEvents}}
              @searchResults={{@searchResults}}
            />
          {{else}}
            <PrismButton
              @buttonType="secondary"
              @icon="search"
              @onClick={{this.setActive}}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
